import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Integration } from '@/api/types';
import { useToast } from '@/components/toast-context';
import { markOnboardingDone } from '@/features/onboarding/state';
import { browser, rememberPending, startConnection, type ConnectInput, type ConnectOutcome } from './connect';

/** Runs a connect attempt and performs the UI side of each outcome. */
export function useConnectFlow(next: string = '/') {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ConnectOutcome | null>(null);

  const connect = useCallback(
    async (integration: Pick<Integration, 'id' | 'name' | 'status'>, input?: ConnectInput) => {
      setBusy(true);
      setOutcome(null);
      const out = await startConnection(integration, input);
      setOutcome(out);
      switch (out.kind) {
        case 'redirect':
          rememberPending({ integration_id: integration.id, next });
          browser.assign(out.url);
          return out; // stay busy while the browser leaves
        case 'connected':
          void qc.invalidateQueries();
          markOnboardingDone(integration.id);
          toast({ title: `${integration.name} connected`, description: 'Your numbers will appear in a moment.' });
          navigate(next, { replace: next === '/onboarding' });
          break;
        case 'needs_account':
          void qc.invalidateQueries({ queryKey: ['connections'] });
          navigate(`/connections/${out.connection.id}/account?next=${encodeURIComponent(next)}`);
          break;
        case 'error':
          toast({ tone: 'error', title: `Couldn’t connect ${integration.name}`, description: out.message });
          break;
        default:
          break;
      }
      setBusy(false);
      return out;
    },
    [navigate, next, qc, toast],
  );

  return { connect, busy, outcome, reset: () => setOutcome(null) };
}
